import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';

import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { SubSink } from 'subsink';

import { ColorSchemeService } from '../color-scheme.service';
import { BrandingService } from '../services/branding.service';
import { PermissionService } from '../services/permission.service';


import { DashboardsCardComponent } from './cards/dashboards-card.component';
import { HelpCardComponent } from './cards/help-card.component';
import { QuickLinksCardComponent } from './cards/quick-links-card.component';
import { RecentJobsCardComponent } from './cards/recent-jobs-card.component';
import { ReleasesCardComponent } from './cards/releases-card.component';
import {
  HOME_CARDS,
  HomeCardConfig,
  HomeCardId,
  SavedCardState,
  loadCardStates,
  saveCardStates,
  getDefaultCardStates,
} from './home-cards.config';

const CARD_COMPONENTS: Record<HomeCardId, Type<any>> = {
  'recent-jobs': RecentJobsCardComponent,
  'dashboards': DashboardsCardComponent,
  'quick-links': QuickLinksCardComponent,
  'help': HelpCardComponent,
  'releases': ReleasesCardComponent,
};

interface ActiveCard {
  config: HomeCardConfig;
  component: Type<any>;
}

@Component({
  selector: 'app-home',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    BreadcrumbModule,
    ButtonModule,
    TooltipModule,
    RecentJobsCardComponent,
    DashboardsCardComponent,
    QuickLinksCardComponent,
    HelpCardComponent,
    ReleasesCardComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {

  home: MenuItem = { icon: 'pi pi-home', label: '\u00A0Home', routerLink: '/home' };

  emptyBreadcrumb: MenuItem[] | undefined;

  activeCards: ActiveCard[] = [];

  allCards: HomeCardConfig[] = [];

  settingsOpen = false;

  draggingIndex: number | null = null;

  isDesktop = window.innerWidth >= 992;

  private subs = new SubSink();

  constructor(
    public colorScheme: ColorSchemeService,
    private permissionService: PermissionService,
    private branding: BrandingService,
  ) {}

  ngOnInit(): void {
    this.allCards = HOME_CARDS.filter((c) =>
      !c.permission || this.permissionService.checkPermission(c.permission),
    );

    this.loadCards();

    this.subs.sink = this.branding.currentZone().subscribe(() => {
      this.loadCards();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  toggleSettings(): void {
    this.settingsOpen = !this.settingsOpen;
  }

  closeSettings(): void {
    this.settingsOpen = false;
  }

  get settingsCards(): HomeCardConfig[] {
    const visible = this.activeCards.map((ac) => ac.config);
    const visibleIds = new Set(visible.map((c) => c.id));
    const hidden = this.allCards.filter((c) => !visibleIds.has(c.id));
    return [...visible, ...hidden];
  }

  isCardVisible(id: HomeCardId): boolean {
    return this.activeCards.some((ac) => ac.config.id === id);
  }

  cardPosition(id: HomeCardId): number {
    return this.activeCards.findIndex((ac) => ac.config.id === id);
  }

  toggleCard(id: HomeCardId): void {
    const isVisible = this.isCardVisible(id);
    if (isVisible) {
      this.activeCards = this.activeCards.filter((ac) => ac.config.id !== id);
    } else {
      const config = this.allCards.find((c) => c.id === id);
      if (config) {
        this.activeCards.push({ config, component: CARD_COMPONENTS[id] });
      }
    }
    this.persist();
  }

  moveCard(id: HomeCardId, direction: -1 | 1): void {
    const index = this.cardPosition(id);
    if (index < 0) { return; }
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.activeCards.length) { return; }
    const tmp = this.activeCards[index];
    this.activeCards[index] = this.activeCards[newIndex];
    this.activeCards[newIndex] = tmp;
    this.persist();
  }

  onDragStart(index: number): void {
    this.draggingIndex = index;
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    if (this.draggingIndex === null || this.draggingIndex === index) { return; }
    const dragged = this.activeCards[this.draggingIndex];
    this.activeCards.splice(this.draggingIndex, 1);
    this.activeCards.splice(index, 0, dragged);
    this.draggingIndex = index;
  }

  onDragEnd(): void {
    this.draggingIndex = null;
    this.persist();
  }

  private loadCards(): void {
    const saved = loadCardStates();
    let states: SavedCardState[];

    if (saved) {
      const savedMap = new Map(saved.map((s) => [s.id, s]));
      states = this.allCards.map((c) => ({
        id: c.id,
        visible: savedMap.get(c.id)?.visible ?? c.defaultVisible,
        order: savedMap.get(c.id)?.order ?? HOME_CARDS.findIndex((hc) => hc.id === c.id),
      }));
    } else {
      states = getDefaultCardStates(HOME_CARDS, this.allCards.filter((c) => c.defaultVisible));
    }

    states.sort((a, b) => a.order - b.order);

    this.activeCards = states
      .filter((s) => s.visible)
      .map((s) => {
        const config = this.allCards.find((c) => c.id === s.id);
        if (!config) { return null; }
        return { config, component: CARD_COMPONENTS[s.id] };
      })
      .filter((c): c is ActiveCard => c !== null);
  }

  private persist(): void {
    const allIds = HOME_CARDS.map((c) => c.id);
    const states: SavedCardState[] = allIds.map((id, i) => ({
      id,
      visible: this.activeCards.some((ac) => ac.config.id === id),
      order: this.activeCards.findIndex((ac) => ac.config.id === id) >= 0
        ? this.activeCards.findIndex((ac) => ac.config.id === id)
        : i + 100,
    }));
    saveCardStates(states);
  }
}